import { useState, useEffect } from 'react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, IconButton, SxProps, Theme } from '@mui/material';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    flex: 1,
    backgroundImage:
      'url(https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-warehouse-background.webp)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    display: {
      xs: 'none',
      sm: 'none',
      md: 'flex',
    },
  },
  decorationsOverlay: {
    zIndex: 5,
    '&::before': {
      content: '""',
      position: 'absolute',
      height: '100%',
      width: '100%',
      left: 0,
      top: 0,
      backgroundColor: (theme) => theme.palette.primary.main,
      opacity: 0.6,
      zIndex: 6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      height: '100%',
      width: '100%',
      left: 0,
      top: 0,
      backgroundColor: (theme) => theme.palette.primary.main,
      WebkitMaskImage: (theme) =>
        `-webkit-linear-gradient(left, rgba(0,0,0,0), ${theme.palette.primary.main})`,
      zIndex: 7,
    },
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 8,
  },
  imageCarousel: {
    zIndex: 10,
    position: 'relative',
    height: '396px',
    width: '619px',
    '@media (max-width: 1250px)': {
      height: 'auto',
      width: '100%',
    },
  },
  carouselImageItem: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    transition: 'opacity 1s ease-in-out',
  },
  carouselSteps: {
    position: 'absolute',
    bottom: '20px',
    zIndex: 10,
  },
  activeStep: {
    color: 'white',
  },
  inactiveStep: {
    color: 'white',
    opacity: '0.6',
  },
};

const carouselImageSteps = [
  {
    imageUrl:
      'https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-carousel-1.webp',
    imageKey: 'carousel-image-1',
    label: 'image of a worker in a yellow hat using DataScope on a tablet',
  },
  {
    imageUrl:
      'https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-carousel-2.webp',
    imageKey: 'carousel-image-2',
    label: 'image of a worker in an orange hat using DataScope on a tablet',
  },
  {
    imageUrl:
      'https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-carousel-3.webp',
    imageKey: 'carousel-image-3',
    label: 'image of a worker in a clean suit using DataScope on a tablet',
  },
  {
    imageUrl:
      'https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-carousel-4.webp',
    imageKey: 'carousel-image-4',
    label: 'image of a worker in a safety jacket using DataScope on a tablet',
  },
];

export function ImageCarousel() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prevActiveStep) => (prevActiveStep + 1) % carouselImageSteps.length);
    }, 4000); // Change image every 3 seconds

    return () => {
      clearInterval(timer);
    };
  }, [activeStep]); // Add activeStep to dependency array

  return (
    <Box sx={customStyles.root}>
      <Box sx={customStyles.decorationsOverlay}>
        <Box
          component='img'
          src='https://data-scope.s3.us-west-2.amazonaws.com/vendors/public/login/login-decorations-svg.webp'
          alt='Decorative image overlay of blue triangles'
          sx={customStyles.image}
          loading='lazy'
        />
      </Box>
      <Box sx={customStyles.imageCarousel}>
        {carouselImageSteps.map((step, index) => (
          <Box
            key={step.imageKey}
            component='img'
            sx={{
              ...customStyles.carouselImageItem,
              opacity: activeStep === index ? 1 : 0,
            }}
            src={step.imageUrl}
            alt={step.label}
            loading='lazy'
          />
        ))}
      </Box>
      <Box sx={customStyles.carouselSteps}>
        {carouselImageSteps.map((_step, index) => (
          <IconButton
            size='small'
            onClick={() => setActiveStep(index)}
            sx={activeStep === index ? customStyles.activeStep : customStyles.inactiveStep}
            key={`step-${index}-carousel`}
          >
            <FiberManualRecordIcon />
          </IconButton>
        ))}
      </Box>
    </Box>
  );
}
